import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Download, FileText, Check, Plus, Trash2, PoundSterling, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { UKDatePicker } from '../ui/date-picker-uk';
import OrderChip from '../Brand/OrderChip';
import { SkeletonRows } from '../Brand/BrandLoading';
import EmptyState from '../ui/empty-state';
import { ukDate, money } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];

export const PayRun = ({ token }) => {
  const [period, setPeriod] = useState('this_month');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [selected, setSelected] = useState({});
  const [payment, setPayment] = useState({ method: 'bank transfer', reference: '', paid_date: '' });
  const [adjustment, setAdjustment] = useState({});
  const [busy, setBusy] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const query = useCallback(() => {
    const params = new URLSearchParams({ period });
    if (period === 'custom') {
      params.set('start_date', custom.start);
      params.set('end_date', custom.end);
    }
    return params.toString();
  }, [period, custom]);

  const load = useCallback(async () => {
    if (period === 'custom' && (!custom.start || !custom.end)) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pay-run?${query()}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load the pay run');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const toggleLine = (email, line) => {
    const key = `${line.type}:${line.id}`;
    setSelected((prev) => {
      const forPerson = { ...(prev[email] || {}) };
      if (forPerson[key]) delete forPerson[key]; else forPerson[key] = line;
      return { ...prev, [email]: forPerson };
    });
  };

  const markPaid = async (person, allOwed) => {
    const chosen = Object.values(selected[person.staff_email] || {});
    if (!allOwed && chosen.length === 0) {
      toast.error('Tick the lines you are paying, or use "Pay all owed"');
      return;
    }
    setBusy(person.staff_email);
    try {
      const body = {
        staff_email: person.staff_email,
        all_owed: allOwed,
        lines: allOwed ? null : chosen.map((l) => ({ type: l.type, id: l.id })),
        period,
        start_date: period === 'custom' ? custom.start : null,
        end_date: period === 'custom' ? custom.end : null,
        method: payment.method,
        reference: payment.reference || null,
        paid_date: payment.paid_date || null,
      };
      const res = await axios.post(`${API}/admin/pay-run/mark-paid`, body, { headers });
      toast.success(res.data.message);
      setSelected((prev) => ({ ...prev, [person.staff_email]: {} }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record that payment');
    } finally {
      setBusy(null);
    }
  };

  const addAdjustment = async (person) => {
    const draft = adjustment[person.staff_email] || {};
    if (!draft.amount || !draft.note) {
      toast.error('Enter an amount and a note');
      return;
    }
    setBusy(`adj-${person.staff_email}`);
    try {
      await axios.post(`${API}/admin/pay-adjustments`, {
        staff_email: person.staff_email,
        amount: parseFloat(draft.amount),
        note: draft.note,
      }, { headers });
      toast.success('Adjustment added');
      setAdjustment({ ...adjustment, [person.staff_email]: {} });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add that adjustment');
    } finally {
      setBusy(null);
    }
  };

  const removeAdjustment = async (id) => {
    try {
      await axios.delete(`${API}/admin/pay-adjustments/${id}`, { headers });
      toast.success('Adjustment removed');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove that adjustment');
    }
  };

  const download = async (url, filename) => {
    try {
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error('Could not build that download');
    }
  };

  return (
    <div className="space-y-4" data-testid="pay-run">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`pay-period-${p.key}`}
            className={`min-h-9 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
              period === p.key ? 'bg-gold text-ink border-gold'
                : 'bg-white text-slate-600 border-hair hover:border-gold hover:bg-cream'
            }`}
          >
            {p.label}
          </button>
        ))}

        {period === 'custom' && (
          <div className="flex items-end gap-2">
            <UKDatePicker label="From" value={custom.start} onChange={(value) => setCustom({ ...custom, start: value })} testId="pay-custom-start" />
            <UKDatePicker label="To" value={custom.end} onChange={(value) => setCustom({ ...custom, end: value })} testId="pay-custom-end" />
          </div>
        )}

        <Button
          variant="outline" size="sm" className="ml-auto"
          onClick={() => download(`${API}/admin/pay-run/export?${query()}`, 'butler-direct-pay-run.csv')}
          data-testid="pay-export-csv"
        >
          <Download className="w-4 h-4 mr-2" /> Pay run CSV
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card className="bd-card-accent bg-white"><CardContent className="p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Owed</p>
            <p className="mt-1 text-2xl font-black tabular-nums text-ink" data-testid="pay-total-owed">{money(data.totals.owed)}</p>
          </CardContent></Card>
          <Card className="bg-white"><CardContent className="p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Paid</p>
            <p className="mt-1 text-2xl font-black tabular-nums text-green-700" data-testid="pay-total-paid">{money(data.totals.paid)}</p>
          </CardContent></Card>
          <Card className="bg-white"><CardContent className="p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Staff this period</p>
            <p className="mt-1 text-2xl font-black tabular-nums text-ink">{data.totals.staff_count}</p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Payment method</label>
          <select
            className=""
            value={payment.method}
            onChange={(e) => setPayment({ ...payment, method: e.target.value })}
            data-testid="pay-method-select"
          >
            <option value="bank transfer">Bank transfer</option>
            <option value="cash">Cash</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Reference (optional)</label>
          <Input value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} className="h-9 w-48" data-testid="pay-reference-input" />
        </div>
        <UKDatePicker label="Paid date (optional)" value={payment.paid_date} onChange={(value) => setPayment({ ...payment, paid_date: value })} testId="pay-date-input" />
      </div>

      {loading && <SkeletonRows rows={4} testId="pay-loading" />}

      {!loading && data?.staff?.length === 0 && (
        <EmptyState
          icon={PoundSterling}
          title="Nothing to pay yet"
          line="Completed jobs in this period will appear here, ready to pay."
          testId="pay-empty"
        />
      )}

      {!loading && data?.staff?.map((person) => (
        <Card key={person.staff_email} className="bd-card-accent bg-white" data-testid={`pay-person-${person.staff_email}`}>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                className="flex items-center gap-2 text-left"
                onClick={() => setOpen({ ...open, [person.staff_email]: !open[person.staff_email] })}
                data-testid={`pay-toggle-${person.staff_email}`}
              >
                {open[person.staff_email] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <span className="font-bold text-ink">{person.staff_name}</span>
                <span className="text-xs text-slate-500">{person.lines.length} job{person.lines.length === 1 ? '' : 's'}</span>
              </button>

              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-amber-100 text-amber-800 tabular-nums" data-testid={`pay-owed-${person.staff_email}`}>Owed {money(person.owed_total)}</Badge>
                <Badge className="bg-green-100 text-green-800 tabular-nums">Paid {money(person.paid_total)}</Badge>
                <Button
                  size="sm" variant="outline"
                  onClick={() => download(`${API}/admin/pay-run/statement?staff_email=${encodeURIComponent(person.staff_email)}&${query()}`, `statement-${person.staff_email}.pdf`)}
                  data-testid={`pay-pdf-${person.staff_email}`}
                >
                  <FileText className="w-4 h-4 mr-1" /> PDF
                </Button>
                <Button
                  size="sm" className="bg-green-600 hover:bg-green-700"
                  disabled={busy === person.staff_email || person.owed_total === 0}
                  onClick={() => markPaid(person, true)}
                  data-testid={`pay-all-${person.staff_email}`}
                >
                  {busy === person.staff_email ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Pay all owed
                </Button>
                <Button
                  size="sm" variant="outline"
                  disabled={busy === person.staff_email}
                  onClick={() => markPaid(person, false)}
                  data-testid={`pay-selected-${person.staff_email}`}
                >
                  Pay selected
                </Button>
              </div>
            </div>

            {open[person.staff_email] && (
              <div className="space-y-3">
                <div className="max-h-[65vh] overflow-auto rounded-[10px] border border-hair">
                  <table className="bd-table text-xs">
                    <thead>
                      <tr>
                        <th className="w-6"></th>
                        <th>Date</th>
                        <th>Order</th>
                        <th>Job</th>
                        <th className="bd-num">Wage</th>
                        <th className="bd-num">Travel</th>
                        <th className="bd-num">Bonus</th>
                        <th className="bd-num">Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {person.lines.map((line) => (
                        <tr key={line.id} data-testid={`pay-line-${line.id}`}>
                          <td>
                            {line.status === 'owed' && (
                              <Checkbox
                                checked={!!(selected[person.staff_email] || {})[`job:${line.id}`]}
                                onCheckedChange={() => toggleLine(person.staff_email, line)}
                                data-testid={`pay-line-check-${line.id}`}
                              />
                            )}
                          </td>
                          <td className="whitespace-nowrap">{ukDate(line.date)}</td>
                          <td><OrderChip value={line.order_number} fallback="—" /></td>
                          <td>{line.job_type}<span className="text-slate-400"> · {line.client_name}</span></td>
                          <td className="bd-num">{money(line.wage)}</td>
                          <td className="bd-num">
                            {money(line.travel)}
                            {line.travel_is_manual && <span className="text-amber-700 ml-1">manual</span>}
                          </td>
                          <td className="bd-num">
                            {money(line.bonus)}
                            {line.bonus_awaiting_photo && <span className="text-slate-400 ml-1">awaiting photo</span>}
                          </td>
                          <td className="bd-num font-bold">{money(line.total)}</td>
                          <td>
                            {line.status === 'paid'
                              ? <span className="whitespace-nowrap text-green-700">Paid {ukDate(line.payment?.paid_date)} · {line.payment?.method}</span>
                              : <span className="font-semibold text-amber-700">Owed</span>}
                          </td>
                        </tr>
                      ))}

                      {person.adjustments.map((adj) => (
                        <tr key={adj.id} data-testid={`pay-adjustment-${adj.id}`}>
                          <td>
                            {adj.status === 'owed' && (
                              <Checkbox
                                checked={!!(selected[person.staff_email] || {})[`adjustment:${adj.id}`]}
                                onCheckedChange={() => toggleLine(person.staff_email, { ...adj, total: adj.amount })}
                              />
                            )}
                          </td>
                          <td className="whitespace-nowrap">{ukDate(adj.date)}</td>
                          <td>—</td>
                          <td colSpan={4}>Adjustment: {adj.note}</td>
                          <td className="bd-num font-bold">{money(adj.amount)}</td>
                          <td>
                            {adj.status === 'paid'
                              ? <span className="text-green-700">Paid {ukDate(adj.payment?.paid_date)}</span>
                              : (
                                <button onClick={() => removeAdjustment(adj.id)} className="text-red-600 hover:underline flex items-center gap-1">
                                  <Trash2 className="w-3 h-3" /> remove
                                </button>
                              )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Adjustment £ (minus for a deduction)</label>
                    <Input
                      type="number" step="0.01" className="h-9 w-32"
                      value={(adjustment[person.staff_email] || {}).amount || ''}
                      onChange={(e) => setAdjustment({ ...adjustment, [person.staff_email]: { ...(adjustment[person.staff_email] || {}), amount: e.target.value } })}
                      data-testid={`adjustment-amount-${person.staff_email}`}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-slate-500 block mb-1">Note</label>
                    <Input
                      placeholder="e.g. parking at the venue"
                      className="h-9"
                      value={(adjustment[person.staff_email] || {}).note || ''}
                      onChange={(e) => setAdjustment({ ...adjustment, [person.staff_email]: { ...(adjustment[person.staff_email] || {}), note: e.target.value } })}
                      data-testid={`adjustment-note-${person.staff_email}`}
                    />
                  </div>
                  <Button
                    size="sm" variant="outline"
                    disabled={busy === `adj-${person.staff_email}`}
                    onClick={() => addAdjustment(person)}
                    data-testid={`adjustment-add-${person.staff_email}`}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add adjustment
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <p className="text-xs text-slate-400 flex items-center gap-1">
        <PoundSterling className="w-3 h-3" /> Paid lines are locked and never appear as owed again. Bonuses stay in step with the Images screen.
      </p>
    </div>
  );
};

export default PayRun;
